import { Box, Flex } from '@chakra-ui/react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineArrowRightOnRectangle } from 'react-icons/hi2';
import {
  Button,
  DrawerComponent,
  TabMenu,
  type DrawerComponentProps,
  type TabItem,
} from '@themes/aegix';

/**
 * A profile tab: the chrome ({@link TabItem}: value / label / optional icon)
 * plus a `render()` slot that returns the tab body. The slot is how each app
 * injects its own sections (AIM: photo / consent / accessibility / devices /
 * duty; SMS: reunification) alongside the shared ones — keeping app-specific
 * code in the app, which is what lets `profile-ui` stay a `type:lib` (it never
 * imports app hooks).
 */
export interface ProfileTab {
  value: string;
  label: string;
  icon?: TabItem['icon'];
  'data-e2e'?: string;
  render: () => ReactNode;
}

export interface ProfileDrawerShellProps {
  isOpen: boolean;
  onClose: () => void;
  /** Tabs, in display order. The first is selected by default. */
  tabs: ProfileTab[];
  onSignOut: () => void;
  title?: string;
  size?: DrawerComponentProps['size'];
  /** Tab `value` to open on first render. Defaults to the first tab. */
  defaultActiveTab?: string;
  /**
   * Controlled active tab. When provided, the shell is controlled —
   * the parent owns the selected-tab state and `onActiveTabChange` must update
   * it. This lets the tab survive a parent remount of the shell (e.g. AIM's
   * site sub-drawer swap). When omitted the shell stays uncontrolled, holding
   * the selection in internal state (SMS path — unaffected).
   */
  activeTab?: string;
  /** Called with the next tab `value` when controlled (see {@link activeTab}). */
  onActiveTabChange?: (value: string) => void;
  signOutLabel?: string;
  e2eTitle?: string;
  /**
   * Optional footer content rendered ABOVE the Sign Out button.
   * Both apps inject their app-specific Reset Password affordance here so it
   * lives in the drawer footer alongside Sign Out rather than inside a tab.
   * Optional, so existing callers that pass neither are unaffected.
   */
  footerSlot?: ReactNode;
}

/**
 * The shared Profile drawer scaffold (SMS's single-layer UX,
 * generalized): a `@themes/aegix` `DrawerComponent` hosting a `TabMenu` over
 * the supplied tabs, with a destructive Sign Out footer. Holds only the
 * active-tab selection (when uncontrolled); everything else comes in via
 * props/slots.
 */
export function ProfileDrawerShell({
  isOpen,
  onClose,
  tabs,
  onSignOut,
  title,
  size = 'md',
  defaultActiveTab,
  activeTab: controlledActiveTab,
  onActiveTabChange,
  signOutLabel,
  e2eTitle = 'profile-drawer',
  footerSlot,
}: ProfileDrawerShellProps) {
  const { t } = useTranslation();
  const displayTitle = title ?? t('profile.title', t('layout.profile', 'Profile'));
  const displaySignOutLabel =
    signOutLabel ?? t('sign_out', t('profile.signOut', 'Sign Out'));

  // Uncontrolled fallback: only consulted when `controlledActiveTab` is
  // undefined, so SMS (which passes neither prop) keeps its internal state.
  const [internalActiveTab, setInternalActiveTab] = useState<string>(
    defaultActiveTab ?? tabs[0]?.value ?? '',
  );
  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;
  const handleTabChange = (value: string) => {
    if (!isControlled) setInternalActiveTab(value);
    onActiveTabChange?.(value);
  };
  const current = tabs.find((t) => t.value === activeTab) ?? tabs[0];

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={displayTitle}
      size={size}
      e2eTitle={e2eTitle}
      footer={
        <Flex gap={3} width="100%">
          {footerSlot && <Box flex={1}>{footerSlot}</Box>}
          <Box flex={1}>
            <Button
              visual="alert"
              width="100%"
              onClick={onSignOut}
              data-e2e="profileDrawerSignOut"
            >
              <Flex align="center" justifyContent="center" gap={2}>
                <HiOutlineArrowRightOnRectangle size={18} />
                {displaySignOutLabel}
              </Flex>
            </Button>
          </Box>
        </Flex>
      }
    >
      <TabMenu
        tabs={tabs.map((t) => ({
          value: t.value,
          label: t.label,
          icon: t.icon,
          'data-e2e': t['data-e2e'],
        }))}
        activeTab={current?.value ?? ''}
        onChange={handleTabChange}
        e2eTitle="ProfileDrawerTabs"
      />

      <Box pt={4}>{current?.render()}</Box>
    </DrawerComponent>
  );
}

export default ProfileDrawerShell;
